import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { PmAccessLogEntry, PmVisitStatus } from '@/types';

const STATUS_COLOURS: Record<PmVisitStatus, string> = {
  granted: 'bg-[var(--status-green)]',
  checked_out: 'bg-[var(--text-secondary)]',
  denied: 'bg-[var(--status-red)]',
  expired: 'bg-[var(--status-amber)]',
  pending: 'bg-[var(--status-blue)]',
};

interface LiveFeedProps {
  entries: PmAccessLogEntry[];
  loading?: boolean;
  onSelect: (entry: PmAccessLogEntry) => void;
}

export function LiveFeed({ entries, loading, onSelect }: LiveFeedProps) {
  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-8">No recent access events</p>
    );
  }

  return (
    <ol className="space-y-1 max-h-[420px] overflow-y-auto pr-1">
      {entries.map((entry) => (
        <li
          key={entry.id}
          onClick={() => onSelect(entry)}
          className="flex items-center gap-3 rounded-md px-3 py-2 cursor-pointer hover:bg-muted/60 transition-colors"
        >
          <span className={cn('h-2 w-2 rounded-full shrink-0', STATUS_COLOURS[entry.status])} />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate">{entry.visitorName}</p>
            <p className="text-xs text-muted-foreground truncate">
              {entry.hostUnit} · {entry.gateName}
            </p>
          </div>
          <span className="text-xs text-muted-foreground shrink-0">
            {new Date(entry.timestamp).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </li>
      ))}
    </ol>
  );
}
